import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import Layout from "../layouts/Layout";
// Importación de iconos para la funcionalidad "mostrar/ocultar contraseña"
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import ForgotPasswordModal from "../components/ForgotPasswordModal";
import ResetPasswordModal from "../components/ResetPasswordModal";

// --- CONSTANTES GLOBALES DE VALIDACIÓN ---
const USERNAME_MAX_LENGTH = 12; // Límite de caracteres para el campo Usuario (Documento)
const PASSWORD_MAX_LENGTH = 12; // Límite de caracteres para la Contraseña
const ALPHANUMERIC_REGEX = /^[a-zA-Z0-9]*$/; // Expresión regular que solo permite letras y números

export default function Login() {
    // Hooks de React Router para navegación y obtención de parámetros
    const navigate = useNavigate();
    const location = useLocation();
    const params = new URLSearchParams(location.search);

    // Detección de parámetros para modales de reseteo de contraseña
    const showResetModal = params.get("reset") === "true";
    const token = params.get("token");
    const email = params.get("email");

    // Estados del componente
    const [tipoDocumento, setTipoDocumento] = useState("CC");
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [mensaje, setMensaje] = useState(""); // Mensajes de error del servidor
    const [showPassword, setShowPassword] = useState(false);
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [errors, setErrors] = useState({}); // Errores de validación de frontend

    // ----------------------------------------------------
    // LÓGICA DE MANEJO DE INPUTS
    // ----------------------------------------------------

    /**
     * Handler para el campo USERNAME (Número de Documento).
     * Aplica filtro alfanumérico y convierte a mayúsculas.
     */
    const handleUsernameChange = (e) => {
        const input = e.target.value;
        
        // 1. Validar que solo sean alfanuméricos
        if (input === "" || ALPHANUMERIC_REGEX.test(input)) {
            // 2. Limitar la longitud al máximo permitido
            if (input.length <= USERNAME_MAX_LENGTH) {
                // Actualiza el estado y convierte a mayúsculas (útil si el backend es sensible a mayúsculas)
                setUsername(input.toUpperCase());
                
                // Limpia el error de validación si la entrada es válida
                if (errors.username) {
                    setErrors(prev => ({ ...prev, username: '' }));
                }
            }
        }
    };

    /**
     * Handler para el campo PASSWORD.
     * Aplica filtro alfanumérico y límite de caracteres.
     */
    const handlePasswordChange = (e) => {
        const input = e.target.value;

        // 1. Validar que solo sean alfanuméricos
        if (input === "" || ALPHANUMERIC_REGEX.test(input)) {
            // 2. Limitar la longitud al máximo permitido
            if (input.length <= PASSWORD_MAX_LENGTH) {
                // Actualiza el estado sin conversión a mayúsculas
                setPassword(input);
            }
        }
    };

    /**
     * Realiza la validación de los campos antes de enviar el formulario.
     * @returns {object} Objeto con los errores detectados.
     */
    const validateForm = () => {
        const newErrors = {};
        if (!tipoDocumento) newErrors.tipoDocumento = "Por favor, selecciona un tipo de documento.";

        if (!username) {
            newErrors.username = "Por favor, completa este campo.";
        } else if (username.length > USERNAME_MAX_LENGTH) {
            newErrors.username = `El usuario no puede exceder los ${USERNAME_MAX_LENGTH} caracteres.`;
        }
        
        if (!password) {
            newErrors.password = "Por favor, completa este campo.";
        } else if (password.length > PASSWORD_MAX_LENGTH) {
            newErrors.password = `La contraseña no puede exceder los ${PASSWORD_MAX_LENGTH} caracteres.`;
        }

        return newErrors;
    };

    /**
     * Maneja el envío del formulario de login.
     */
    const handleSubmit = async (e) => {
        e.preventDefault();
        setMensaje(""); // Limpiar mensajes de error previos
        const formErrors = validateForm();
        
        // Si hay errores de validación de frontend, muestra los mensajes y detiene el envío.
        if (Object.keys(formErrors).length > 0) {
            setErrors(formErrors);
            return;
        }
        setErrors({}); // Limpia errores si la validación es exitosa

        try {
            const loginData = {
                tipo_documento: tipoDocumento,
                username: username,
                password: password,
            };

            console.log("Enviando datos de login:", loginData);
            // Realiza la petición POST a la API
            const response = await axios.post(`/login`, loginData); 

            console.log("Respuesta del servidor:", response.data);

            // Almacenamiento de token y datos de usuario
            localStorage.setItem("token", response.data.token);
            localStorage.setItem("user", JSON.stringify(response.data.user));

            // Redirige al dashboard
            navigate("/dashboard");
        } catch (error) {
            // Manejo de errores de conexión o del servidor (ej: Error 500, 401)
            console.error("Error en el login:", error.response ? error.response.data : error.message);
            if (error.response) {
                setMensaje("❌ " + (error.response.data.message || "Error desconocido al iniciar sesión."));
            } else {
                setMensaje("❌ Error de conexión con el servidor.");
            }
        }
    };

    // ----------------------------------------------------
    // RENDERIZADO DEL COMPONENTE
    // ----------------------------------------------------

    return (
        <Layout>
            <div className="bg-white/5 backdrop-blur-sm p-10 rounded-2xl shadow-xl w-[700px]">
                <h1 className="text-center text-white text-xl font-bold bg-secondary rounded-lg py-3 mb-6 shadow-md">
                    Portal de Empleados - Piedecuestana E.S.P.
                </h1>

                {/* --- SECCIÓN DE TIPO DE DOCUMENTO --- */}
                {/* Código omitido por brevedad, no hay cambios funcionales aquí */}
                {/* ... */}
                <div className="flex justify-center items-center mb-1">
                    <label className="flex justify-center items-center text-gray-700 font-medium">
                        Tipo de documento
                    </label>
                </div>
                <div className="flex flex-col items-center mb-4">
                    <select
                        className="w-70 px-4 py-2 rounded-lg bg-primary text-white font-semibold shadow-md hover:bg-secondary focus:ring-2 focus:ring-accent outline-none cursor-pointer transition"
                        value={tipoDocumento}
                        onChange={(e) => setTipoDocumento(e.target.value)}
                    >
                        {/* Opciones del select... */}
                        <option value="" disabled>Seleccione...</option>
                        <option value="CC">Cédula de Ciudadanía (CC)</option>
                        <option value="TI">Tarjeta de Identidad (TI)</option>
                        <option value="CE">Cédula de Extranjería (CE)</option>
                        <option value="NIT">NIT</option>
                        <option value="PA">Pasaporte</option>
                        <option value="PEP">Permiso Especial de Permanencia (PEP)</option>
                        <option value="DNI">Documento Nacional de Identidad (DNI)</option>
                    </select>
                    {errors.tipoDocumento && <p className="text-red-500 text-xs mt-1">{errors.tipoDocumento}</p>}
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* --- CAMPO USUARIO (CORREGIDO) --- */}
                    <div>
                        <label className="block text-gray-700 font-medium mb-1">Usuario</label>
                        <input
                            type="text"
                            placeholder="Número de documento"
                            value={username}
                            // ❗ ESTO FUE CORREGIDO: SE AÑADE EL HANDLER DE CAMBIO
                            onChange={handleUsernameChange}
                            maxLength={USERNAME_MAX_LENGTH}
                            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-accent outline-none"
                        />
                        {errors.username && <p className="text-red-500 text-xs mt-1">{errors.username}</p>}
                    </div>

                    {/* --- CAMPO CONTRASEÑA --- */}
                    <div>
                        <label className="block text-gray-700 font-medium mb-1">Contraseña</label>
                        <div className="relative">
                            <input
                                type={showPassword ? "text" : "password"}
                                placeholder="********"
                                value={password}
                                // Handler de la contraseña (con filtro alfanumérico y límite)
                                onChange={handlePasswordChange}
                                maxLength={PASSWORD_MAX_LENGTH}
                                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-lime outline-none pr-10"
                            />
                            {/* Botón para mostrar/ocultar contraseña */}
                            <button
                                type="button"
                                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                onClick={() => setShowPassword(!showPassword)}
                            >
                                {showPassword ? <EyeSlashIcon className="h-5 w-5 text-gray-600" /> : <EyeIcon className="h-5 w-5 text-gray-600" />}
                            </button>
                        </div>
                        {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
                    </div>

                    {/* --- BOTONES Y MENSAJES --- */}
                    <div className="flex justify-center">
                        <button
                            type="button"
                            onClick={() => setShowForgotPassword(true)}
                            className="text-sm text-secondary hover:text-primary transition bg-transparent border-none cursor-pointer"
                        >
                            ¿Olvidó su contraseña?
                        </button>
                    </div>

                    <button
                        type="submit"
                        className="w-full bg-lime text-white font-bold py-2 rounded-lg hover:bg-secondary transition"
                    >
                        INGRESAR
                    </button>
                </form>

                {mensaje && <p className="mt-4 text-center text-sm font-medium text-red-600">{mensaje}</p>}
            </div>
            
            {/* --- MODALES --- */}
            {showForgotPassword && <ForgotPasswordModal onClose={() => setShowForgotPassword(false)} />}

            {showResetModal && token && email && (
                <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
                    <ResetPasswordModal
                        token={token}
                        email={email}
                        onClose={() => navigate("/login")}
                    />
                </div>
            )}
        </Layout>
    );
}